import React, { Dispatch, useReducer } from "react";
import jwtDecode from "jwt-decode";
import Cookies from "js-cookie";
import {client} from "helpers/client";

const AuthContext = React.createContext<[AuthState,Dispatch<Action>] | undefined>(undefined);
AuthContext.displayName = "AuthContext";

export enum CustomerStatus {
  UnVerified = 1,
  Verified = 2,
  Pending = 3,
  Registered = 4,
  Suspended = 5
}

export interface TokenData {
  Name:string;
  CountryId: string;
  Id: string;
  email: string;
  StatusId: string;
  isSuspended:boolean;
}

type Action =
  | { type: "login"; token: string; staySignedIn: boolean; refresh: string }
  | { type: "logout" };

interface AuthState {
  token: string | null;
  isLoggedIn: boolean;
  user: Partial<TokenData>;
}

const reducer = (state: AuthState, action: Action):AuthState => {
  switch (action.type) {
    case "login":
      if (action.staySignedIn) {
        saveTokens(action.token, action.refresh, addDays(90));
      } else {
        saveTokens(action.token, action.refresh);
      }
      return {
        token: action.token,
        isLoggedIn: true,
        user: jwtDecode(action.token),
      };

    case "logout":
      logout();
      return { token: null, isLoggedIn: false, user: {} };

    default:
      return state;
  }
};

export function saveTokens(token:string,refreshToken:string,expirationDate?:Date) {
  Cookies.set('_token' , token,{expires:expirationDate});
  Cookies.set('_refresh' , refreshToken,{expires:expirationDate});
  expirationDate && Cookies.set('_remember', expirationDate.toString());
}


export function getTokens() {
  return {
    token : Cookies.get('_token'),
    refreshToken : Cookies.get('_refresh'),
    expirationDate : Cookies.get('_remember')
  }
}

export function removeTokens() {
  Cookies.remove('_token');
  Cookies.remove('_refresh');
  Cookies.remove('_remember');
}

export async function logout() {
  try {
    const { refreshToken } = getTokens();
    await client("/auth/logout", { data: { refreshToken } });
  } catch (e) {
  } finally {
    removeTokens();
    window.location.assign("/login");
  }
}

function addDays(days:number) {
  const result = new Date();
  result.setDate(result.getDate() + days);
  return result;
}

export function AuthProvider({ ...props }) {
  const { token } = getTokens();
  const user  = token ? jwtDecode<TokenData>(token) : {} as any;
  user.isSuspended = user.StatusId === CustomerStatus.Suspended.toString();

  const [state, dispatch] = useReducer(reducer, {
    token: token || null,
    isLoggedIn: !!token,
    user
  });

  return <AuthContext.Provider value={[state, dispatch]} {...props} />;
}

export function useAuthContext() {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext hook must be wrapped in AuthProvider");
  }

  return context;
}